import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { trackVideoEvent } from '../utils/analytics';

const HomeContainer = styled.div`
  min-height: 80vh;
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

// Video Hero Section Components
const VideoHeroSection = styled.section`
  background: linear-gradient(135deg, rgba(44, 82, 130, 0.95), rgba(29, 78, 216, 0.95));
  padding: 4rem 0;
  text-align: center;
`;

const VideoContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const VideoTitle = styled(motion.h1)`
  color: white;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  font-weight: bold;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const VideoSubtitle = styled(motion.p)`
  color: rgba(255,255,255,0.95);
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
    text-align: left;
  }
`;

const YouTubeHeroContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto 2rem auto;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);

  &::before {
    content: '';
    display: block;
    padding-top: 56.25%; /* 16:9 aspect ratio */
  }
`;

const YouTubeHeroIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoDescription = styled(motion.p)`
  color: rgba(255,255,255,0.9);
  font-size: 1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  font-style: italic;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, rgba(44, 82, 130, 0.95), rgba(29, 78, 216, 0.95));
  color: white;
  padding: 6rem 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  opacity: 0.95;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const GraceQuote = styled(motion.p)`
  font-size: 0.95rem;
  margin-bottom: 2rem;
  opacity: 0.85;
  font-style: italic;
  line-height: 1.5;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroStats = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin: 2rem 0;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Stat = styled.div`
  text-align: center;
  
  .number {
    font-size: 3rem;
    font-weight: bold;
    color: #e53e3e;
    display: block;
  }
  
  .label {
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled(Link)`
  background-color: ${props => props.primary ? '#e53e3e' : 'transparent'};
  color: white;
  padding: 1rem 2rem;
  border: 2px solid ${props => props.primary ? '#e53e3e' : 'white'};
  border-radius: 8px;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  display: inline-block;
  
  &:hover {
    background-color: ${props => props.primary ? '#c53030' : 'white'};
    color: ${props => props.primary ? 'white' : '#2c5282'};
    text-decoration: none;
    transform: translateY(-2px);
  }
`;

const StorySection = styled.section`
  padding: 4rem 0;
  background-color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const StoryHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  color: white;
  border-radius: 16px 16px 0 0;
  max-width: 1200px;
  margin: 0 auto 0 auto;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="1" fill="white" fill-opacity="0.1"><animate attributeName="r" values="1;3;1" dur="3s" repeatCount="indefinite"/></circle></svg>');
    pointer-events: none;
  }
`;

const StoryTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
  font-weight: 700;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  position: relative;
  z-index: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const StorySubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.95;
  font-style: italic;
  line-height: 1.5;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;


const StoryContent = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 2rem;
  }
`;

const JournalEntry = styled.div`
  line-height: 1.6;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  
  h1, h2, h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1rem;
    text-align: justify;
  }
`;

const FamilyPhotoSection = styled.div`
  margin: 3rem 0;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  border: 1px solid rgba(0,0,0,0.05);
`;

const FamilyPhotoTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
`;

const FamilyPhotoContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  
  img {
    width: 100%;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0,0,0,0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 12px 35px rgba(0,0,0,0.25);
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0;
  }
`;

const FamilyPhotoCaption = styled.p`
  margin-top: 1rem;
  font-style: italic;
  color: #666;
  font-size: 0.95rem;
  line-height: 1.4;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  // Helper function to handle video events for analytics
  const handleVideoEvent = (eventType, videoTitle) => {
    trackVideoEvent(eventType, videoTitle);
  };

  return (
    <HomeContainer>
      <SEO
        title="Home"
        description="Follow Maggie Soward's miraculous survival story after sudden cardiac arrest on Thanksgiving Day 2024. A testament to faith, medical expertise, emergency response, and the power of community support. Only 5% survival rate - but miracles do happen."
        url="/"
        image="/uploads/EMTS_AT_WORK.jpg"
        type="website"
      />

      {/* Video Hero Section */}
      <VideoHeroSection>
        <VideoContainer>
          <VideoTitle
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            The Miracle of November 28, 2024
          </VideoTitle>

          <VideoSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Our heroes in action: Emergency responders working tirelessly to save Maggie's life. For 26 minutes, they performed relentless CPR, administered three life-saving defibrillator shocks, and never gave up hope. Their training, dedication, and coordination made the miracle possible.
          </VideoSubtitle>

          <YouTubeHeroContainer
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            onClick={() => handleVideoEvent('hero_video_click', 'Complete Emergency Response - The Full Story')}
          >
            <YouTubeHeroIframe
              src="https://www.youtube.com/embed/HJw0lhrRY6E?rel=0&modestbranding=1&fs=1&autoplay=0&cc_load_policy=0&iv_load_policy=3&color=white&autohide=0&theme=light"
              title="Complete Emergency Response - Maggie's Miracle"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </YouTubeHeroContainer>

          <VideoDescription
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            We had forgotten about a camera that sits on top of our refrigerator that captured almost the entire emergency response on Thanksgiving Day 2024 when Maggie went into cardiac arrest. In the beginning, you can hear our panic, and then Eliza comes down to save her mom by performing CPR. The footage shows the heroic, coordinated efforts of Saratoga Springs EMTs, firefighters, and police officers who refused to give up. Later we learned from Captain Seis Wetherell that "he had a feeling that they were not going to lose her."
          </VideoDescription>
        </VideoContainer>
      </VideoHeroSection>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Maggie's Miracle
          </HeroTitle>
          
          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A story of faith, family, and the miracle of November 28, 2024
          </HeroSubtitle>
          
          <GraceQuote
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Grace is the divine help or strength extended to us through the Atonement of the Lord Jesus Christ. Through the grace of God, everyone who has lived will be resurrected—our spirits will be reunited with our bodies, never again to be separated. Through His grace, the Lord also enables those who live His gospel to repent and be forgiven.
          </GraceQuote>
         
          <HeroStats
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Stat>
              <span className="number">5%</span>
              <span className="label">Chance of Survival</span>
            </Stat>
            <Stat>
              <span className="number">26</span>
              <span className="label">Minutes to Pulse</span>
            </Stat>
            <Stat>
              <span className="number">9</span>
              <span className="label">First Responders</span>
            </Stat>
          </HeroStats>
          
          <CTAButtons
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <CTAButton to="/gallery" primary>
              Heroes & Journey
            </CTAButton>
            <CTAButton to="/spiritual">
              God's Grace
            </CTAButton>
          </CTAButtons>
        </HeroContent>
      </HeroSection>

      <StorySection>
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Our Story Section - Full Content */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <StoryHeader>
                <StoryTitle>Our Story</StoryTitle>
                <StorySubtitle>
                  On Thanksgiving Day 2024, our family experienced a life-changing emergency that strengthened our faith, brought us closer to our friends, family, and community, and, as we continue to discover, was a miraculous series of events.
                </StorySubtitle>
              </StoryHeader>

              <FamilyPhotoSection>
                <FamilyPhotoTitle>Our Heroes in Action</FamilyPhotoTitle>
                <FamilyPhotoContainer>
                  <OptimizedImage
                    category="emergency-response"
                    filename="EMTS_AT_WORK.jpg"
                    alt="Emergency Medical Technicians at work - Our heroes in action"
                    thumbnail={false}
                    preload={true}
                    style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                  />
                </FamilyPhotoContainer>
                <FamilyPhotoCaption>
                  We love these EMTs! Captain Seis said he had a feeling that he should not give up—that they were going to save Maggie's life.<br/>
                  Captain Ryan Rackman, Firefighter/AEMT Alec Olsen, Engineer Ivan Radcliff, Critical Care Paramedic Isaac Ostler, Eliza Soward, Firefighter/EMT Josh Cluff, Captain Seis Wetherell, Engineer David Perry, Paramedic Abby Walz (lead).
                </FamilyPhotoCaption>
              </FamilyPhotoSection>

              <StoryContent>
                <JournalEntry>
                    <hr style={{ margin: '30px 0', border: 'none', borderTop: '2px solid #e2e8f0' }} />
                    
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Today, as we look back on the incredible journey we've been through, we feel a deep sense of gratitude. It's been nearly seven months since the events on Thanksgiving Day, November 28, 2024, since "Maggie's death", and now, many months later, we are still trying to piece together all the details that went into her survival. This site is our way of trying to recognize all the miracles that aligned perfectly, and to begin to express our gratitude for the emergency responders, and the outpouring of faith and love from our community.</p>

                    <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>The Beginning: Thanksgiving Morning</h2>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>It began early that Thursday morning, between 8:00 and 10:00 AM. Isaac and I were up Israel Canyon, practicing with his new .22 rifle. When we returned, I began preparing for our Goblin Valley campout that weekend (planned for November 29–December 1) with the boys—Isaac, Noah, Declan, and Ian. I was in the kitchen, researching Dutch oven soup recipes, and calling into Maggie who was resting on her bed in the master bedroom. I was asking her how many potatoes I should take. Maggie decided to get up and walk into the pantry. We later learned that her Fitbit showed an elevated heart rate at that time. In the pantry, she knelt down and picked out three large potatoes for our meal.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Then, at 12:20 PM, everything changed. She handed me the third potato, said, "I feel like I'm going to pass out," and collapsed onto my left shoe. I told her repeatedly "Breathe Maggie". Her fingers and toes began twisting in a seizure, and as I rolled her over, her eyes were shut and her face was gray. Dread and panic shot through me, as I desperately tried to wake her up and began to shout her name.</p>

                    <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>The Emergency Response</h2>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At 12:21 PM, Realizing she was not breathing, I tried, what I thought, was CPR and mouth-to-mouth. I told Isaac to GET Eliza, (who'd just recertified for the 3rd time in CPR 6 days prior at MTech). Ike told me he was going to call 911. Noah shouted upstairs to Eliza, "Mom needs CPR!" as Eliza rushed down in a towel (from her shower). At 12:22 PM, she entered the pantry, said to me "move, move, move", and took over. She calmly, went to work, checking for a pulse—none—and began 30 chest compressions for nearly five minutes. Isaac, who had the 911 dispatcher on the phone,  handed me the phone. I held it while Eliza worked, the dispatcher counting "1, 2, 3, 4" and asked us to check for breathing, as Eliza continued chest compressions counting 1 to 30. Maggie moaned, a sound that was horrifying. We watched Maggie for any sign of life and Eliza continue. The dispatcher instructed us to unlock the door and secure the pets.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At 12:23 PM, Noah, with tears in his eyes, started texting hundreds of contacts: "Please prey for my mom, we think she just had a heart attack." In that moment of complete helplessness his faith was in God as his prayer mobilization stretched across states.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>By 12:25 PM, EMT's arrived followed by, police, firefighters, all within 5-6 minutes of Ike's call. Isaac guided them to Maggie, and they took over Eliza's heroic efforts. From 12:26 to 12:50 PM, they proceeded life saving efforts. They slid Maggie frm the pantry to the kitchen, moved the dinning room table, each taking turns administering CPR, and delivered three shocks from the defibrillator. During the course of this, and as Eliza and I were watching with shock, one of the officers escorted us to the front room. From there we received periodic updates on Maggie's progress. Maggie occassionally would moan, a long deep moanthat scared us even more. After 26 minutes of PERSISTANT and AMAZING coordination, training, and lifesaving efforts, they detected a weak pulse, loaded her onto a gurney, gave her an IV through the femoral artery, and rushed her to the ER.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At ~12:55 PM, Isabel and Declan, who were 15 mins away and received a call from Eliza, arrived just as the ambulance left. Isable was understandably distraught, and between 12:50 and 1:00 PM, she and I were directed to the couch by a member of the EMT's, and held eachother while we listened to one of the emergency workers describe for us what had just taken place and what was going to happen. While all the comotion was taking place, downstairs Noah got the girls to each say a prayer for their mother. Anna stepped up, by watching and caring for her sisters with such tenderness during the chaos.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At ~1:00 PM, our amazing and loving neighbors Heather Jordan and Kirsten Hall and Tiffany Bryant came in, went downstairs to the girls and cared for the kids, while many other neighbors waited outside. Eliza drove, me, and Isaac to Lehi Mountain Point Hospital.</p>

                    <FamilyPhotoSection>
                      <FamilyPhotoTitle>The Potato That Got Her</FamilyPhotoTitle>
                      <FamilyPhotoContainer>
                        <img
                          src="/uploads/IMG_0120_optimized.jpeg"
                          alt="The potato that got her"
                        />
                      </FamilyPhotoContainer>
                      <FamilyPhotoCaption>
                        Friday, the day after this event, Noah was in the pantry, exactly where Maggie collapsed, and was overheard by Declan somberly saying "this is the potato that got her."
                      </FamilyPhotoCaption>
                    </FamilyPhotoSection>

                    <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>Hospital Journey</h2>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>From 1:00 to 2:00 PM, as we drove in a daze to the ER, I called Ben to let him know what just happened. When we got to the hospital, we were surprised and grateful to be met in the parking lot by Bishop Clint Nelson who was already there. Everything is still a blur, but the ER nurse confirmed that Maggie was on a ventilator and her pulse was being monitored. Bishop Nelson and I gave Maggie a blessing. I anointed her, and Bishop Nelson blessed her, while Chaplain Dave offered comfort. Maggie, with a breathing tube and IVs, seemed lifeless. I blessed Noah too. That evening, she was moved to the ICU under Cardiologists Dr. Ghali and Dr. Al Jandali's care. At that point our biggest fear was brain damage from lack of blood to the brain during Maggie's time in the pantry.</p>

                    <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>Recovery Week: Day by Day</h2>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>November 29 (Friday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At 4:00 PM, doctors said she was doing really well. They fed her through a tube, weaned her off drugs to reduce sedation—she couldn't breathe on her own while sedated—and planned to remove the tube the next day. One of the very exciting and encouraging moments was when, with the reduced sedation, she communicated with her eyebrows, squeezed our hands, and wiggled her toes. We knew she was still in there. I wrote, "I think she's gonna be OK." Maggie in her sedated state attempted multiple times to pull her breathing tube out. The ICU nurses strapped her arms down so she could not, however Maggie, known since birth to be relentlessly stubborn, did not give up and eventually by reaching with her hands and leaning forward, all while still sedated, pulled her breathing tube out. Removing it this way (without deflating) damaged her windpipe causing her to whisper for weeks. Later that evening, Ingrid, her sister, had the first meaningful conversation with her, where Maggie talked about knowing she was with her mom and her brother and being told to go back. Visitors that first day: Ron, Marlese, Lee, Brit, Bethany.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>November 30 (Saturday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>We were all so happy that she was alive and without brain damage!! Throughout the day, her short-term memory began returning. We had many conversations with her while she was semi sedated and it was hilarious. Doctors got her moving, gave her light solid food, and evaluated her throat, but no discharge. She walked the ICU floor, used the bathroom alone, and had visits all day, though propofol left her loopy with spotty memory. After multiple exams, the doctors found that part of her heart wasn't functioning, ventricular tachycardia, causing arrhythmia, and scheduled a cardiologist for Monday. We were so overwhelmed, it was all such a terrifying experience, but we knew it was an incredible blessing she was still with us, and began to recognize that there were multiple miracles along the way. Maggie later in the evening woke up, and she and I began talking. She told me through tears that Christ is full of grace and mercy and love. We talked and cried together.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>December 1 (Sunday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>She was still loopy, her short-term memory not fully back but better. The cardiologist confirmed the apex of her heart wasn't working, which caused atrial fibrillation (AFib). This happened two additional times while in the hospital (not nearly as severe since she was being monitored), and we discussed options for how to help her heart. We decided on an ICD (An Implantable Cardioverter Defibrillator (ICD) is a small, battery-powered device placed in the chest to monitor and correct life-threatening irregular heart rhythms). She was transferred by ambulance to St. Mark's Hospital ICU. Meanwhile, Violet courageously gave a beautiful testimony that Sunday in church, thanking our amazing neighbors. Eliza also bore her testimony and spoke of the many miracles that occurred to save her mom's life.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>December 2 (Monday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>At 2:13 PM, we arrived at room 5N22. Maggie was doing great, and had most of her memory back. She was extremely sore, her ribs were cracked, and her arm damaged during the CPR. They told her that her arm would be in a sling for weeks post-ICD surgery, with the date unclear until additional evaluations.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>December 3 (Tuesday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Another echocardiogram showed the akinetic region was now small, this was great news meaning that the lower region of her heart that was not working was getting smaller. Unfortunately, the cardiologist could not compare prior recordings from Mountain Point. She had an MRI at 4:00 PM on her heart. They planned the ICD surgery for December 4.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>December 4 (Wednesday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>We waited all day for surgery to start at 6:10 PM. It was finished at 8:20 PM, a success by Dr. Nannapaneni. Prior to surgery, Ben Lambert gave her a blessing and Ingrid talked with her. The implant surgery was a success and we started making plans for when she could leave and what home life those first few days would look like.</p>

                    <h3 style={{ color: '#4a6741', fontSize: '1.3rem', marginTop: '25px', marginBottom: '10px' }}>December 5 (Thursday)</h3>
                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Discharge papers started. We expected to be home by 4:00 PM. Maggie was cleared and discharged. Eliza drove up to pick us up from the hosptial.</p>

                    <FamilyPhotoSection>
                      <FamilyPhotoTitle>Our Eternal Family</FamilyPhotoTitle>
                      <FamilyPhotoContainer>
                        <OptimizedImage
                          src="/uploads/LalaDecTemple.jpeg"
                          alt="Soward family at the temple for Lala and Declan's sealing"
                          thumbnail={false}
                          style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                        />
                      </FamilyPhotoContainer>
                      <FamilyPhotoCaption>
                        The Soward family at the temple celebrating Lala and Declan's sealing - a beautiful reminder of the eternal bonds that strengthen us through life's greatest challenges.
                      </FamilyPhotoCaption>
                    </FamilyPhotoSection>

                    <h2 style={{ color: '#2c3e50', fontSize: '1.5rem', marginTop: '30px', marginBottom: '15px' }}>Reflections on Faith and Miracles</h2>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>As I write this, I'm struck by the convergence of so many miraculous elements: Eliza's recent CPR training, her telling me about CPR training in the car a week prior, Maggie coming to the kitchen, that fact that she fell on my left foot, Isaac being home to call 911, Noah's immediate prayer mobilization, the swift response of first responders on a holiday, and the countless prayers from our community. Each detail orchestrated by a loving Heavenly Father who knew what our family would need.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>The medical team called it a 5% survival rate for what Maggie experienced. But we know it was more than medicine, it was a heavenly plan and an amazing tapestry of precisely coordinated details and events, it was faith, family, community, and divine intervention working together to keep Maggie here with us.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Maggie's experience on the other side of the veil, her conversations with her mom who told her "Get your butt back!" and her overwhelming feeling when she returned of Christ's grace and mercy have strengthened our entire family's faith. Her request while praying all summer was, if anything happened to her, that she be given the opportunity to raise her children, and that prayer was answered in the most miraculous way.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>Maggie attributes much of the help that she received from the other side of the veil to those individuals whose work and ordinances she has researched and performed on FamilySearch.org and in temples.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>As we share this story, we do so with hearts full of gratitude for Maggie's life, for our children's courage, for our community's love, and for a God who hears and answers prayers.</p>

                    <p style={{ marginBottom: '20px', textAlign: 'left' }}>We want to share this experience with everyone who prayed, fasted, served, and believed. Thank you for being part of Maggie's miracle.</p>

                    <hr style={{ margin: '30px 0', border: 'none', borderTop: '2px solid #e2e8f0' }} />

                    <p style={{ fontStyle: 'italic', opacity: 0.8, marginBottom: '15px' }}>Written with love and gratitude,</p>
                    <p style={{ fontWeight: 600, color: '#27ae60', marginBottom: '15px' }}>Scott Soward</p>
                  </JournalEntry>
                </StoryContent>
              </motion.div>
            </motion.div>
          </Container>
        </StorySection>
        <NextSectionContainer>
        {/* <NextSectionText>
          Continue with the spiritual perspective of this miraculous event...
        </NextSectionText> */}
        <NextSectionButton href="/gallery">
          Heroes & Journey →
        </NextSectionButton>
      </NextSectionContainer>
      </HomeContainer>
    );
  };

export default Home;
